/**
 * The home page's sections, each one the answer to a question a person walks
 * in with (roadmap E-7; docs/9-exploration/PRD-personal-and-team-questions.md).
 *
 * Pure functions over rows the stores already hold. Nothing here fetches; the
 * page decides what to load, these decide what to say. A section whose list
 * is empty is not drawn (build contract §2.8).
 */

import type { ResponsibilityCard } from '../../../types/prioritization/responsibility';
import { isResponsibilityValid } from '../../../types/prioritization/responsibility';
import type { StoredNotification } from '../../../lib/notificationService';
import type { Decision } from '../../../types/prioritization/decisions';
import { duePhrase } from './oneThing';
import { formatRelativeTime } from '../../../i18n/formatters';
import { normalizeLoopState, type CatchballLoopState } from '../../../types/prioritization/catchballLoop';

const DAY_MS = 86_400_000;

function byDeadline(a: ResponsibilityCard, b: ResponsibilityCard): number {
  if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
  if (a.deadline) return -1;
  if (b.deadline) return 1;
  return (a.created_at ?? '').localeCompare(b.created_at ?? '');
}

// ── P2 · What else needs me ─────────────────────────────────────────────────

export interface NeedsMeRow {
  card: ResponsibilityCard;
  subject: string;
  /** Why it needs the person, in the engine's words. */
  why: string;
  due: string | null;
}

/** Every REQUIRED card after the one thing, soonest first. */
export function moreThatNeedsMe(cards: ResponsibilityCard[], today: Date = new Date()): NeedsMeRow[] {
  const required = cards.filter(isResponsibilityValid).filter((c) => c.classification === 'REQUIRED').sort(byDeadline);
  return required.slice(1).map((card) => ({ card, subject: card.subject, why: (card.why ?? '').trim(), due: duePhrase(card.deadline, today) }));
}

// ── The line under the greeting names what needs me ────────────────────────

export interface NeedsYouThing {
  id: string;
  /** What it is, as a reader would name it: "the Q3 Priorities — Live Ranking session". */
  label: string;
  href: string | null;
}

/**
 * A card as the line under the greeting names it. A subject is often a name or
 * a rationale ("Ranked by impact in the walkthrough session."), which does not
 * say what kind of thing waits, so the kind is said around it.
 */
export function needsYouThing(card: ResponsibilityCard, href: string | null): NeedsYouThing {
  let s = card.subject.trim().replace(/[.\s]+$/, '');
  if (s.length > 60) s = `${s.slice(0, 57).trimEnd()}…`;
  const label =
    card.subject_type === 'session' ? (/\bsession$/i.test(s) ? s : `the ${s} session`)
    : card.subject_type === 'proposal' ? `the proposal “${s}”`
    : card.subject_type === 'ball' || card.subject_type === 'loop' ? `the ball on ${s}`
    : s;
  return { id: card.id, label, href };
}

/**
 * The opening of the line under the greeting, and the things it names. A count
 * alone tells nobody what to do, so the first `shown` are named (each a link)
 * and the rest counted: "Two things need you: A and B." /
 * "5 things need you, starting with A and B."
 */
export function needsYouLine(things: NeedsYouThing[], shown = 2): { lead: string; named: NeedsYouThing[] } {
  const n = things.length;
  const lead =
    n === 0 ? 'Nothing needs you.'
    : n === 1 ? 'One thing needs you:'
    : n === 2 ? 'Two things need you:'
    : n <= shown ? `${n} things need you:`
    : `${n} things need you, starting with`;
  return { lead, named: things.slice(0, shown) };
}

// ── P3 · What am I waiting on ───────────────────────────────────────────────

export interface WaitingRow {
  card: ResponsibilityCard;
  subject: string;
  /** Who has it, in the engine's words ("Waiting for executive review"). */
  why: string;
  /** The person holding it, when the stores can name one. */
  holderId: string | null;
  /** Since when they have held it (ISO), when known. */
  since: string | null;
}

/** What the home already holds in its stores, to say who has each waiting thing. */
export interface WaitingContext {
  proposals?: { id: string; loopId?: string | null }[];
  loops?: { id: string; ballHolderUserId?: string | null; ballClaimedAt?: string | null }[];
  sessions?: { id: string; facilitatorId?: string | null }[];
}

/**
 * The WAITING cards the engine already computes: the ball is with someone else.
 * Each row names who holds it when the stores can say: a proposal's loop holder
 * (since migration 862 the executive who will decide), or a session's
 * facilitator.
 */
export function waitingOnOthers(cards: ResponsibilityCard[], ctx: WaitingContext = {}): WaitingRow[] {
  const proposalLoop = new Map((ctx.proposals ?? []).map((p) => [p.id, p.loopId ?? null]));
  const loops = new Map((ctx.loops ?? []).map((l) => [l.id, l]));
  const sessions = new Map((ctx.sessions ?? []).map((x) => [x.id, x]));
  return cards
    .filter(isResponsibilityValid)
    .filter((c) => c.classification === 'WAITING')
    .map((card) => {
      let holderId: string | null = null;
      let since: string | null = null;
      const subjectId = card.subject_id ?? null;
      if (subjectId && card.subject_type === 'proposal') {
        const loopId = proposalLoop.get(subjectId);
        const loop = loopId ? loops.get(loopId) : undefined;
        holderId = loop?.ballHolderUserId ?? null;
        since = loop?.ballClaimedAt ?? null;
      } else if (subjectId && card.subject_type === 'loop') {
        const loop = loops.get(subjectId);
        holderId = loop?.ballHolderUserId ?? null;
        since = loop?.ballClaimedAt ?? null;
      } else if (subjectId && card.subject_type === 'session') {
        holderId = sessions.get(subjectId)?.facilitatorId ?? null;
      }
      return { card, subject: card.subject, why: (card.why ?? '').trim(), holderId, since };
    });
}

/** "With Dana Reyes since yesterday", or the engine's own words when nobody can be named. */
export function waitingLine(row: WaitingRow, holderName: string | null): string {
  if (!holderName) return row.why;
  return row.since ? `With ${holderName} since ${formatRelativeTime(row.since)}` : `With ${holderName}`;
}

// ── P4 · What has changed that impacts me ───────────────────────────────────

export interface ChangedLine {
  id: string;
  title: string;
  body: string;
  when: string;
  /** ISO time, for ordering against decisions in "what moved". */
  at: string;
  href: string | null;
}

/** One thing that changed, with every unread change to it, newest first. */
export interface ChangedRow extends ChangedLine {
  /** The subject these changes are about, when the notifications name one (migration 864). */
  subjectKey: string | null;
  /** The earlier unread changes to the same thing. */
  earlier: ChangedLine[];
  /** The same news about other things ("A critical finding needs a look", once per finding). */
  alike: ChangedLine[];
}

function toLine(n: StoredNotification): ChangedLine {
  return {
    id: n.id,
    title: n.payload.title,
    body: n.payload.body ?? '',
    when: formatRelativeTime(n.createdAt),
    at: n.createdAt.toISOString(),
    href: n.payload.actionUrl ?? null,
  };
}

/**
 * The notifications this person has not read yet, grouped by the thing they
 * are about, newest thing first. The database routes each event to the people
 * it concerns (migrations 737, 745, 820) and, since 864, names its subject on
 * the row and tells an item's watchers when it changes.
 *
 * The same news about several things is one row, not one per thing: a rule
 * that fires once per finding sent seven identical lines, which read as alarm
 * and said nothing. The first carries the row; the rest are `alike`, so the
 * page can name each thing once under one heading. A notice whose subject has
 * moved on (finished, 1009/1037: the finding it named was replaced) is not news.
 */
export function whatHasChangedForMe(notifications: StoredNotification[], limit = 8): ChangedRow[] {
  const unread = notifications
    .filter((n) => !n.read && !n.dismissed && !n.finishedAt)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const groups = new Map<string, ChangedRow>();
  const byTitle = new Map<string, ChangedRow>();
  const order: ChangedRow[] = [];
  for (const n of unread) {
    const key = n.payload.subjectType && n.payload.subjectId ? `${n.payload.subjectType}:${n.payload.subjectId}` : null;
    const existing = key ? groups.get(key) : undefined;
    if (existing) {
      existing.earlier.push(toLine(n));
      continue;
    }
    const same = byTitle.get(n.payload.title);
    if (same) {
      same.alike.push(toLine(n));
      continue;
    }
    if (order.length >= limit) continue;
    const row: ChangedRow = { ...toLine(n), subjectKey: key, earlier: [], alike: [] };
    if (key) groups.set(key, row);
    byTitle.set(n.payload.title, row);
    order.push(row);
  }
  return order;
}

const BULLET = /^[•·*-]\s+/;

/**
 * What a row names, as short points the page draws one per line. A notice
 * that lists things ("Beacon flagged 7 external signals…:" then a bullet per
 * signal, then "Review and accept or dismiss in the app.") contributes only
 * its bullets: the lead restates the title and the closing line repeats the
 * button. Drawn as one paragraph, its line breaks fell away and it read as a
 * wall. A notice that is one sentence contributes that sentence. The same
 * point from two notices is named once.
 */
export function changedPoints(row: ChangedRow): { listed: boolean; points: string[] } {
  const listed: string[] = [];
  const plain: string[] = [];
  for (const line of [row, ...row.alike]) {
    const lines = line.body.split('\n').map((l) => l.trim()).filter(Boolean);
    const bullets = lines.filter((l) => BULLET.test(l)).map((l) => l.replace(BULLET, ''));
    if (bullets.length > 0) listed.push(...bullets);
    else if (lines.length > 0) plain.push(lines.join(' '));
  }
  const points = Array.from(new Set([...listed, ...plain]));
  return { listed: listed.length > 0 || points.length > 1, points };
}

/** Every notification a row stands for, to clear them together. */
export function changedIds(row: ChangedRow): string[] {
  return [row.id, ...row.earlier.map((e) => e.id), ...row.alike.map((a) => a.id)];
}

// ── P5 · What is coming up that I am part of ────────────────────────────────

export interface UpcomingRow {
  id: string;
  kind: 'session' | 'review';
  name: string;
  when: string;
  /** ISO time, for ordering sessions and reviews together. */
  at: string;
  href: string;
}

interface SessionLike {
  id: string;
  name?: string | null;
  state?: string;
  scheduledAt?: string | Date | null;
  participantIds?: string[];
}

/** Sessions in the next 14 days that this person is in, soonest first. */
export function comingUpForMe(sessions: SessionLike[], userId: string, today: Date = new Date()): UpcomingRow[] {
  const horizon = today.getTime() + 14 * DAY_MS;
  return sessions
    .filter((s) => s.participantIds?.includes(userId))
    .filter((s) => s.state !== 'PUBLISHED')
    .map((s) => ({ s, at: s.scheduledAt ? new Date(s.scheduledAt) : null }))
    .filter((x): x is { s: SessionLike; at: Date } => !!x.at && !Number.isNaN(x.at.getTime()) && x.at.getTime() > today.getTime() && x.at.getTime() < horizon)
    .sort((a, b) => a.at.getTime() - b.at.getTime())
    .map(({ s, at }) => ({
      id: s.id,
      kind: 'session' as const,
      name: s.name || 'Session',
      when: at.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
      at: at.toISOString(),
      href: `/prioritization/sessions/${s.id}`,
    }));
}

/** Reviews due on decisions the person made, within the next 90 days, soonest first. */
export function reviewsDue(decisions: Decision[], userId: string, today: Date = new Date(), horizonDays = 90): UpcomingRow[] {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const horizon = start + horizonDays * DAY_MS;
  return decisions
    .filter((d) => d.status === 'active' && !!d.reviewByDate && String(d.decidedBy ?? '') === userId)
    .map((d) => ({ d, at: new Date(d.reviewByDate as string) }))
    .filter(({ at }) => !Number.isNaN(at.getTime()) && at.getTime() >= start && at.getTime() < horizon)
    .sort((a, b) => a.at.getTime() - b.at.getTime())
    .map(({ d, at }) => ({
      id: d.id,
      kind: 'review' as const,
      name: `Review your decision: ${d.title}`,
      when: at.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
      at: at.toISOString(),
      href: `/prioritization/decisions/${d.id}`,
    }));
}

/** P5 · sessions the person is in and reviews of their own decisions, soonest first. */
export function comingUp(sessions: SessionLike[], decisions: Decision[], userId: string, today: Date = new Date()): UpcomingRow[] {
  return [...comingUpForMe(sessions, userId, today), ...reviewsDue(decisions, userId, today)].sort((a, b) => a.at.localeCompare(b.at));
}

// ── A loop still in play ───────────────────────────────────────────────────

/** A loop is still in play while it is open, proposed, or sent back for revision. */
const IN_PLAY = new Set(['OPEN', 'PROPOSED', 'REVISION_REQUESTED']);

export function loopInPlay(state: string): boolean {
  return IN_PLAY.has(normalizeLoopState(state as CatchballLoopState));
}

// ── T5 · What did we decide ─────────────────────────────────────────────────

export interface DecidedRow {
  id: string;
  title: string;
  /** What it was about, when the decision names a cycle: "Q4 planning". */
  about: string | null;
  when: string;
  /** ISO time, for ordering against changes in "what moved". */
  at: string;
  href: string;
}

/**
 * The latest standing decisions, newest first. Automatic decisions carry fixed
 * titles ("Catchball proposal accepted"), so each row also says which cycle it
 * was about, from the decision's own cycle_id.
 */
export function whatWeDecided(
  decisions: Decision[],
  limit = 5,
  cycles: { id: string; name: string }[] = [],
): DecidedRow[] {
  const cycleName = new Map(cycles.map((c) => [c.id, c.name]));
  return decisions
    .filter((d) => d.status === 'active')
    .sort((a, b) => (b.decidedAt ?? '').localeCompare(a.decidedAt ?? ''))
    .slice(0, limit)
    .map((d) => ({
      id: d.id,
      title: d.title,
      about: (d.cycleId && cycleName.get(d.cycleId)) || null,
      when: d.decidedAt ? formatRelativeTime(d.decidedAt) : '',
      at: d.decidedAt ?? '',
      href: `/prioritization/decisions/${d.id}`,
    }));
}

// ── What moved: changes that reach me and what we decided, as one list ─────

export type MovedRow =
  | ({ kind: 'change' } & ChangedRow)
  | ({ kind: 'decision' } & DecidedRow);

/** One dated list, newest first: the changes that reached the person and the latest decisions. */
export function whatMoved(changed: ChangedRow[], decided: DecidedRow[], limit = 8): MovedRow[] {
  const rows: MovedRow[] = [
    ...changed.map((c) => ({ kind: 'change' as const, ...c })),
    ...decided.map((d) => ({ kind: 'decision' as const, ...d })),
  ];
  return rows.sort((a, b) => b.at.localeCompare(a.at)).slice(0, limit);
}

// ── P0 · What to set up first (a workspace with nothing in it yet) ──────────

export interface SetupStep {
  id: 'items' | 'cycle' | 'invite' | 'session';
  label: string;
  done: boolean;
  href: string | null;
}

export interface SetupFacts {
  hasItems: boolean;
  hasCycles: boolean;
  hasSessions: boolean;
  /** A personal space has nobody to invite. */
  isPersonal: boolean;
  /** Starting a cycle and inviting people are admin acts. */
  isAdmin: boolean;
}

/**
 * The first steps in a workspace that has not taken them yet — the getting-
 * started list the retired page of widgets showed (DEP-030), carried onto the
 * home so a new workspace still says what to do. Empty once the workspace has
 * items and a session. A step the person may not take is left out, not shown
 * and refused (build contract §2.8).
 */
export function setupSteps(f: SetupFacts): SetupStep[] {
  if (f.hasItems && f.hasSessions) return [];
  const steps: Array<SetupStep & { adminOnly: boolean }> = [
    { id: 'items', label: 'Add your first items', done: f.hasItems, href: '/prioritization/intake/submit', adminOnly: false },
    { id: 'cycle', label: 'Start a prioritization cycle', done: f.hasCycles, href: '/prioritization/cycles', adminOnly: true },
    ...(f.isPersonal ? [] : [{ id: 'invite' as const, label: 'Invite your team', done: false, href: '/prioritization/settings/invites', adminOnly: true }]),
    { id: 'session', label: 'Run your first session', done: f.hasSessions, href: '/prioritization/sessions', adminOnly: false },
  ];
  return steps.filter((s) => !s.adminOnly || f.isAdmin).map(({ adminOnly: _adminOnly, ...s }) => s);
}
